import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t } from '../i18n.js';
import { St } from '../components/Status.jsx';
import ModuleKaarten from '../components/ModuleKaarten.jsx';
import { MODULE_ICOON } from '../components/iconen.jsx';

/**
 * Modules, voor medewerkers (docs/besluiten/modules-per-klant.md): bij *Alle klanten* een overzicht klant × module, bij één
 * klant de modulekaarten. Met `rechten.beheren` hier ook afnemen en aan- of uitzetten, zoals onder Beheer › Klanten ›
 * Modules (dezelfde API); in de tabel met één knop per vak. Aanzetten mag ook als hij nog niet is ingericht; wat er
 * ontbreekt staat erbij (er komen pas alerts als het er is).
 */
const STAND = { aan: ['good', 'aan'], klaar: ['warning', 'klaar'], 'af te maken': ['warning', 'af te maken'], 'niet afgenomen': ['neutral', '—'] };

export default function Modules({ klant, mij }) {
  const q = useQuery({ queryKey: ['modules-overzicht'], queryFn: api.modulesOverzicht, refetchInterval: 60_000 });
  const qc = useQueryClient();
  const beheer = mij.permissions.includes('rechten.beheren');
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(null);
  const zet = async (code, id, invoer) => {
    zetMelding(null); zetBezig(`${code}:${id}`);
    try { await api.zetKlantModule(code, id, invoer); } catch (e) { zetMelding(t(e.message)); } finally {
      zetBezig(null); await q.refetch(); qc.invalidateQueries({ queryKey: ['klant-modules', code] });
    }
  };
  const kop = (
    <div className="page-head"><div>
      <h1>{t('Modules')}</h1>
      <p className="sub">{beheer ? t('Wat elke klant heeft afgenomen en wat er aan staat. Zet een module hier aan of uit.') : t('Wat elke klant heeft afgenomen en wat er aan staat.')}</p>
    </div></div>
  );
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;
  const lijst = q.data.data;
  const fout = melding && <div className="melder rood" style={{ marginBottom: 10 }}>{melding}</div>;
  const een = klant !== 'alle' && lijst.find((x) => String(x.tenant.id) === String(klant));
  if (een) {
    const code = een.tenant.code;
    return (
      <>{kop}<h2 className="sectiekop">{een.tenant.name}</h2>{fout}
        <ModuleKaarten modules={een.modules} bezig={Boolean(bezig)} doe={beheer && !een.tenant.own ? {
          afnemen: (id, afgenomen) => zet(code, id, { afgenomen }),
          schakel: (id, aan) => zet(code, id, { aan }),
        } : {}} ookOningericht /></>
    );
  }
  const modules = lijst[0]?.modules ?? [];
  /* Eén knop per vak: wat de volgende stap is. Basis en de eigen klant staan altijd aan. */
  const vak = (x, m) => {
    const [soort, label] = STAND[m.state];
    const stand = m.state === 'niet afgenomen' ? <span className="muted">—</span> : <St soort={soort}>{t(label)}</St>;
    if (!beheer || m.fixed || x.tenant.own) return stand;
    const knop = (tekst, invoer, primair) => (
      <button type="button" className={`btn small${primair ? ' primary' : ''}`} disabled={Boolean(bezig)}
        onClick={() => zet(x.tenant.code, m.id, invoer)}>{bezig === `${x.tenant.code}:${m.id}` ? t('Bezig…') : tekst}</button>
    );
    return (
      <span className="modvak">{stand}
        {m.state === 'aan' && knop(t('Uitzetten'), { aan: false })}
        {m.state === 'klaar' && knop(t('Aanzetten'), { aan: true }, true)}
        {m.state === 'niet afgenomen' && knop(t('Afnemen'), { afgenomen: true })}
        {m.state === 'af te maken' && <>{knop(t('Aanzetten'), { aan: true })}
          <span className="muted klein" title={m.steps.filter((s) => !s.ok).map((s) => t(s.label)).join(', ')}>{t('nog in te richten')}</span></>}
      </span>
    );
  };
  return (
    <>
      {kop}{fout}
      <section className="card" style={{ overflowX: 'auto' }}>
        <table className="modmatrix">
          <thead><tr><th>{t('Klant')}</th>{modules.map((m) => <th key={m.id}><span className="modicoon klein">{MODULE_ICOON[m.id]}</span>{t(m.label)}</th>)}</tr></thead>
          <tbody>{lijst.map((x) => (
            <tr key={x.tenant.id}>
              <td><b>{x.tenant.name}</b>{x.tenant.own && <span className="muted"> {t('eigen')}</span>}</td>
              {x.modules.map((m) => <td key={m.id}>{vak(x, m)}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </section>
      {beheer && <p className="noot">{t('Uit: geen nieuwe alerts uit die module bij deze klant; wat er al is, blijft. Afnemen gaat nu met de hand; straks komt het uit de contractregels in ADTS-ONE. Kies een klant voor de stappen en Niet meer afnemen.')}</p>}
    </>
  );
}
